import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Globe, Flame } from "lucide-react";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://uktranspet.com";

// Slugs we consider "most booked" — surfaced in the Popular Routes hero section.
const POPULAR_SLUGS = [
  "pet-transport-london-to-paris",
  "pet-transport-folkestone-to-calais",
  "pet-transport-amsterdam-to-london",
  "pet-transport-london-to-brussels",
  "pet-transport-london-to-frankfurt",
  "pet-transport-manchester",
  "pet-transport-edinburgh",
  "pet-transport-london-to-madrid",
];

function bookingUrl(b) {
  const params = new URLSearchParams({
    pickup: b.route_from || "",
    dropoff: b.route_to || "",
    miles: String(b.distance_miles || ""),
  });
  if (b.category === "International") params.set("channel", "1");
  return `/book?${params.toString()}`;
}

export default function Routes() {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/blogs").then(({ data }) => setBlogs(data));
  }, []);

  // Group articles
  const { ukRoutes, intlByCountry, popular } = useMemo(() => {
    const uk = blogs.filter((b) => b.category === "UK Cities");
    const intl = blogs.filter((b) => b.category === "International");
    const byCountry = {};
    for (const b of intl) {
      const c = b.country || "Other";
      (byCountry[c] = byCountry[c] || []).push(b);
    }
    const popularMap = Object.fromEntries(blogs.map((b) => [b.slug, b]));
    const pop = POPULAR_SLUGS.map((s) => popularMap[s]).filter(Boolean);
    return { ukRoutes: uk, intlByCountry: byCountry, popular: pop };
  }, [blogs]);

  const jsonLd = useMemo(() => {
    if (!blogs.length) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "All Pet Transport Routes — UK & Europe",
        description:
          "Complete directory of door-to-door pet transport routes across the United Kingdom and Europe. 60 route guides with live prices.",
        url: `${SITE_URL}/routes`,
        isPartOf: { "@type": "WebSite", name: "UK Pet Transport", url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: blogs.length,
        itemListElement: blogs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}/blog/${b.slug}`,
          name: b.title,
        })),
      },
    ];
  }, [blogs]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="routes-hub">
      <SEO
        title="All Pet Transport Routes — UK & Europe Directory"
        description="Complete list of every pet transport route we operate. 25 UK cities plus 35 international destinations across France, Belgium, Netherlands, Germany, Spain, Italy and more. Prices, drive times, and border info."
        path="/routes"
        jsonLd={jsonLd}
      />

      <div className="max-w-3xl">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Directory</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-3">All pet transport routes.</h1>
        <p className="text-lg text-muted-foreground mt-5">
          Every destination we serve — 25 UK cities and 35 European routes. Pick a destination for pricing, drive time, border paperwork and local vet tips.
        </p>
        <div className="flex gap-3 mt-8">
          <Button asChild data-testid="routes-cta-quote"><Link to="/quote">Get a quote</Link></Button>
          <Button asChild variant="outline" data-testid="routes-cta-book"><Link to="/book">Book a route</Link></Button>
        </div>
      </div>

      {/* POPULAR ROUTES — featured, with direct book buttons */}
      {popular.length > 0 && (
        <section className="mt-16" id="popular">
          <div className="flex items-center gap-3 mb-8">
            <Flame size={18} className="text-accent" />
            <h2 className="font-display text-3xl">Popular routes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {popular.map((b) => (
              <Card
                key={b.slug}
                className="p-5 border-border bg-secondary/40 hover:border-accent/60 transition flex flex-col"
                data-testid={`popular-route-${b.slug}`}
              >
                <Link to={`/blog/${b.slug}`} className="group">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">
                    {b.category === "International" ? b.country : "UK"}
                  </span>
                  <h3 className="font-display text-xl mt-2 leading-snug group-hover:text-accent transition-colors">
                    {b.route_from} ↔ {b.route_to}
                  </h3>
                </Link>
                <p className="text-xs text-muted-foreground mt-3">{b.distance_miles} miles</p>
                <p className="mt-4">
                  <span className="text-xs text-muted-foreground">From</span>{" "}
                  <span className="font-display text-3xl">£{b.price_passat}</span>
                </p>
                <div className="mt-auto pt-5 flex flex-col gap-2">
                  <Button asChild size="sm" className="w-full" data-testid={`popular-book-${b.slug}`}>
                    <Link to={bookingUrl(b)}>
                      Book this route <ArrowRight size={14} className="ml-2" />
                    </Link>
                  </Button>
                  <Link
                    to={`/blog/${b.slug}`}
                    className="text-xs text-muted-foreground hover:text-foreground text-center"
                  >
                    Read route guide
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* UK Cities */}
      {ukRoutes.length > 0 && (
        <section className="mt-20" id="uk-cities">
          <div className="flex items-center gap-3 mb-8">
            <MapPin size={18} className="text-accent" />
            <h2 className="font-display text-3xl">United Kingdom — {ukRoutes.length} cities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {ukRoutes.map((b) => (
              <Card
                key={b.slug}
                className="p-4 border-border hover:border-accent/60 transition flex flex-col"
                data-testid={`routes-uk-card-${b.slug}`}
              >
                <Link to={`/blog/${b.slug}`} className="group">
                  <p className="font-display text-base leading-snug group-hover:text-accent transition-colors" data-testid={`routes-uk-${b.slug}`}>
                    London ↔ {b.route_to}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {b.distance_miles} mi · <span className="text-foreground/80">from £{b.price_passat}</span>
                  </p>
                </Link>
                <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                  <Link
                    to={`/blog/${b.slug}`}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Guide
                  </Link>
                  <Button asChild size="sm" variant="outline" data-testid={`book-route-${b.slug}`}>
                    <Link to={bookingUrl(b)}>Book <ArrowRight size={12} className="ml-1" /></Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* International by country */}
      {Object.keys(intlByCountry).sort().map((country) => (
        <section key={country} className="mt-16" id={`country-${country.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-3 mb-6">
            <Globe size={18} className="text-accent" />
            <h2 className="font-display text-3xl">
              {country} — {intlByCountry[country].length} route{intlByCountry[country].length > 1 ? "s" : ""}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {intlByCountry[country].map((b) => (
              <Card
                key={b.slug}
                className="p-5 border-border hover:border-accent/60 transition flex flex-col"
                data-testid={`routes-intl-card-${b.slug}`}
              >
                <Link to={`/blog/${b.slug}`} className="group">
                  <p className="font-display text-base leading-snug group-hover:text-accent transition-colors" data-testid={`routes-intl-${b.slug}`}>
                    {b.route_from} ↔ {b.route_to}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {b.distance_miles} mi · <span className="text-foreground/80">from £{b.price_passat}</span>
                  </p>
                </Link>
                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                  <Link
                    to={`/blog/${b.slug}`}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Read guide
                  </Link>
                  <Button asChild size="sm" variant="outline" data-testid={`book-route-${b.slug}`}>
                    <Link to={bookingUrl(b)}>Book this route <ArrowRight size={12} className="ml-1" /></Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}

      {/* CTA footer */}
      <section className="mt-24 border-t border-border pt-16">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl">Don't see your route?</h2>
          <p className="text-muted-foreground mt-3">
            We travel anywhere in the UK and continental Europe. If your destination isn't listed, request a quote — we'll price it for you within 30 minutes.
          </p>
          <div className="flex gap-3 mt-6">
            <Button asChild><Link to="/quote">Request a custom quote</Link></Button>
            <Button asChild variant="outline"><Link to="/blog">Read the journal</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
